import type { ProductType } from '../../models/rate-sheet.models';

/**
 * Phase 10.1 — DimKey × ProductType applicability matrix.
 * A pinned DimKey that's false for the current ProductType silently
 * hides in the Pinned section (per spec P3).
 *
 * Note: `ProductType` (from rate-sheet.models) includes 'ALL' alongside
 * the four product tabs. The 'ALL' column is set to true for every dim
 * so that the cross-tab view shows all pinned dims; spec P3 only
 * dictates behavior for FA/IA/RILA/VA.
 */
export type DimKey =
  | 'states'
  | 'carriers'
  | 'premium'
  | 'surrYrs'
  | 'indexes'
  | 'strategies'
  | 'strategyCombos'
  | 'amBest'
  | 'riders'
  | 'premiumTypes'
  | 'bonus'
  | 'mva'
  | 'rop'
  | 'inverse'
  | 'owner'
  | 'annuitant'
  | 'faRateTerm'
  | 'faSurrYears'
  | 'faBonusYears'
  // RILA/VA filter block (2026-06-05) — six new dims, all RILA/VA only
  | 'bufferLevel'
  | 'floorLevel'
  | 'segmentDuration'
  | 'rateType'
  | 'minPremium'
  | 'protectionType';

export const DIM_TAB_APPLICABILITY: Record<DimKey, Record<ProductType, boolean>> = {
  states:         { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  carriers:       { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  premium:        { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  surrYrs:        { FA: true,  IA: true,  RILA: false, VA: true,  ALL: true },
  indexes:        { FA: false, IA: true,  RILA: true,  VA: true,  ALL: true },
  strategies:     { FA: false, IA: true,  RILA: true,  VA: true,  ALL: true },
  strategyCombos: { FA: false, IA: true,  RILA: true,  VA: true,  ALL: true },
  amBest:         { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  riders:         { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  premiumTypes:   { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  bonus:          { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  mva:            { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  rop:            { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  inverse:        { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  owner:          { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  annuitant:      { FA: true,  IA: true,  RILA: true,  VA: true,  ALL: true },
  // FA-only dims — applicable on FA only, so a pin made on FA stays
  // hidden on the other tabs (and in the cross-tab ALL view).
  faRateTerm:     { FA: true,  IA: false, RILA: false, VA: false, ALL: false },
  faSurrYears:    { FA: true,  IA: false, RILA: false, VA: false, ALL: false },
  faBonusYears:   { FA: true,  IA: false, RILA: false, VA: false, ALL: false },
  // RILA/VA filter block (2026-06-05) — applicable only on RILA + VA tabs.
  bufferLevel:      { FA: false, IA: false, RILA: true,  VA: true,  ALL: false },
  floorLevel:       { FA: false, IA: false, RILA: true,  VA: true,  ALL: false },
  segmentDuration:  { FA: false, IA: false, RILA: true,  VA: true,  ALL: false },
  rateType:         { FA: false, IA: false, RILA: true,  VA: true,  ALL: false },
  minPremium:       { FA: false, IA: false, RILA: false, VA: true,  ALL: false },
  protectionType:   { FA: false, IA: false, RILA: true,  VA: true,  ALL: false },
};

export function isApplicable(dim: DimKey, tab: ProductType): boolean {
  return DIM_TAB_APPLICABILITY[dim][tab];
}
